// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { recentErrorMessages, reportContext, track } from "../../lib/metrics.js";
import { allFaqs, faqsForAudience, groupByCategory, searchFaqs } from "../../lib/faq.js";
import { t as ct } from "../../lib/content.js";
import { apiUrl } from "../../lib/origin.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------------- help ------------------------------------ */
/*
 * Help & support (docs/HELP_SCOPE.md). One sheet, two views: browse/search the FAQ, or
 * tell us something. The report view is reachable from the top of the browse view, from
 * a failed search, and directly (openTo="report") from the app shell's error strip and
 * the persistent footer link.
 *
 * What changed and why (Zip 28):
 *   - The corpus moved to the CMS. It used to be a hardcoded array here, which is how
 *     the Savings Vault answer stayed factually wrong for weeks after W1.6 removed the
 *     bank-account concept it described — every correction needed a deploy.
 *   - Audience filtering. One flat list shown identically to a six-year-old and a
 *     parent worked at 21 entries and does not at 70. Kid Mode opens on "For kids".
 *   - Search, because a scrollable accordion of ten categories is not findable.
 *   - Reporting is no longer only for bugs. "This doesn't make sense" is a first-class
 *     option, deliberately — those are the reports nothing else in the stack can tell
 *     us about, and the ones most likely to precede someone quietly stopping.
 */

export function FaqItem({ entry, open, onToggle, onFeedback }) {
  const [rated, setRated] = useState(false);
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden" data-testid="faq-item" data-faq-id={entry.id}>
      <button
        className="w-full flex items-center justify-between text-left px-3 py-2.5 text-base font-semibold text-slate-700"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{entry.q}</span>
        <span className="text-slate-400 text-lg leading-none flex-shrink-0 ml-2">{open ? "−" : "+"}</span>
      </button>
      {/* whitespace-pre-line: a plain <div> collapses \n to nothing, which would silently
          flatten the bulleted answers into one run-on line. This preserves line breaks
          while still collapsing accidental double-spaces within a line. */}
      {open && (
        <div className="px-3 pb-3">
          <div className="text-base text-slate-500 leading-relaxed whitespace-pre-line">{entry.a}</div>
          {/* The only signal we get on whether an answer actually works. Deliberately
              tiny and unmissable-but-ignorable — a required rating would just be noise. */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
            {rated ? (
              <span className="text-xs text-emerald-600 font-semibold">{ct("help.helpful_thanks", "Thanks. That helps us write better answers.")}</span>
            ) : (
              <>
                <span className="text-xs text-slate-400 font-medium">{ct("help.helpful_prompt", "Did that help?")}</span>
                <button className="text-sm px-2 py-0.5 rounded-lg border border-slate-200" aria-label="Yes, that helped" onClick={() => { setRated(true); onFeedback(entry.id, true); }}>{ct("help.helpful_yes", "Yes")}</button>
                <button className="text-sm px-2 py-0.5 rounded-lg border border-slate-200" aria-label="No, that didn't help" onClick={() => { setRated(true); onFeedback(entry.id, false); }}>{ct("help.helpful_no", "No")}</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// The three things a person might want to tell us, in the order they're likely to want
// them. "Something's broken" first because it's the most urgent; "doesn't make sense"
// second because it's the most common and the one people previously had no words for.
const REPORT_KINDS = [
  { id: "broken", icon: "ic-attention", hue: "amber", labelKey: "report.kind_broken", label: "Something's broken", subKey: "report.kind_broken_sub", sub: "It crashed, froze, or did the wrong thing" },
  { id: "confusing", icon: "ic-help", hue: "indigo", labelKey: "report.kind_confusing", label: "Something doesn't make sense", subKey: "report.kind_confusing_sub", sub: "Confusing, hard to find, or just odd" },
  { id: "idea", icon: "ic-wand", hue: "gold", labelKey: "report.kind_idea", label: "I've got an idea", subKey: "report.kind_idea_sub", sub: "Something you'd like us to add or change" },
];

// Posts to /api/feedback. Email is OPTIONAL — see that handler's header for why the
// old mandatory field was actively losing us reports (a child literally could not
// submit one). In Kid Mode we don't ask for an email at all: the household owner is the
// reply address, which is both the right answer under the AADC and the practical one.
export function ReportForm({ code, role = "parent", ownerEmail = "", defaultKind = "confusing", searchQuery = "", errorHint = "", showToast, onDone }) {
  const [kind, setKind] = useState(defaultKind);
  const [email, setEmail] = useState(role === "child" ? "" : ownerEmail || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sentId, setSentId] = useState(null);

  useEffect(() => { track("report_opened", { kind: defaultKind }); }, [defaultKind]);

  async function submit() {
    const trimmedMessage = message.trim();
    const trimmedEmail = email.trim();
    if (!trimmedMessage) {
      showToast(ct("report.need_message", "Add a line about what happened first"), "bad");
      return;
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      showToast(ct("report.bad_email", "That email doesn't look right — fix it, or clear it and send anyway"), "bad");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(apiUrl("/api/feedback"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          role,
          message: trimmedMessage,
          email: role === "child" ? "" : trimmedEmail,
          ownerEmail: ownerEmail || "",
          code: code || "",
          // Everything the user shouldn't have to describe: build, screen, mode, device,
          // online state and any errors already caught this session (see
          // metrics.reportContext — no names, no balances, no photos).
          context: reportContext({ searchQuery, errors: errorHint ? [errorHint, ...recentErrorMessages()].slice(0, 5) : undefined }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setSentId(data.id || "sent");
      track("report_submitted", { kind });
    } catch (err) {
      showToast(err.message || ct("report.failed", "Couldn't send. Check your connection and try again"), "bad");
    } finally {
      setSending(false);
    }
  }

  if (sentId) {
    return (
      <div className="rounded-lg bg-white border border-slate-200 px-3 py-4 text-center" data-testid="report-sent">
        <div className="text-emerald-700 text-base font-semibold">{ct("report.sent", "Got it, thank you. Your reference is {id}.", { id: sentId })}</div>
        <div className="text-emerald-600 text-xs mt-1">{ct("report.sent_sub", "A real person reads every one of these.")}</div>
        {onDone && (
          <button className="mt-3 text-sm font-bold text-emerald-700 underline" onClick={onDone}>{ct("help.back_to_help", "Back to help")}</button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="report-form">
      <p className="text-sm text-slate-500 leading-relaxed">{ct("report.intro", "We'd much rather hear about it than have you quietly give up on something. Anything at all is useful.")}</p>
      <div className="flex flex-col gap-1.5">
        {REPORT_KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            data-testid={`report-kind-${k.id}`}
            aria-pressed={kind === k.id}
            onClick={() => setKind(k.id)}
            className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left ${kind === k.id ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"}`}
          >
            <span className="leading-none flex-shrink-0"><Icon id={k.icon} size={18} hue={k.hue} /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-slate-800">{ct(k.labelKey, k.label)}</span>
              <span className="block text-xs text-slate-400">{ct(k.subKey, k.sub)}</span>
            </span>
          </button>
        ))}
      </div>
      <label className="text-xs font-medium text-slate-500">{ct("report.message_label", "What were you trying to do?")}</label>
      <textarea
        data-testid="report-message"
        placeholder={ct("report.message_placeholder", "As much or as little as you like.")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        className="border border-slate-200 rounded-lg px-3 py-2 text-base resize-none"
      />
      {role === "child" ? (
        <p className="text-xs text-slate-400 leading-relaxed">{ct("report.kid_note", "This goes to the people who make Little Bankers, and we'll reply to the grown-up who set up your household.")}</p>
      ) : (
        <>
          <label className="text-xs font-medium text-slate-500">{ct("report.email_label", "Email (optional, only so we can reply)")}</label>
          <input
            type="email"
            inputMode="email"
            data-testid="report-email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-base"
            autoComplete="email"
          />
          <p className="text-xs text-slate-400 -mt-1">{ct("report.email_note", "Leave it blank if you'd rather not. We'll still read it.")}</p>
        </>
      )}
      <button
        onClick={submit}
        disabled={sending}
        data-testid="report-send"
        className="w-full px-3 py-2.5 rounded-lg text-base font-semibold text-white bg-slate-900 disabled:opacity-60"
      >
        {sending ? ct("report.sending", "Sending…") : ct("report.send", "Send it")}
      </button>
    </div>
  );
}

export function HelpModal({ onClose, code, showToast, mode = "parent", ownerEmail = "", openTo = "faq", errorHint = "" }) {
  const [view, setView] = useState(openTo === "report" ? "report" : "faq");
  // Kid Mode opens on kid answers, Parent Mode on parent answers, and "Everything" is
  // always one tap away — the audience tag is a good guess, not a lock. A parent looking
  // up what their child is seeing needs the other side, and so does anyone the guess
  // got wrong.
  const [audience, setAudience] = useState(mode === "child" ? "kid" : "parent");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [openCats, setOpenCats] = useState(() => new Set());

  useEffect(() => { track("help_opened", { mode: mode === "child" ? "kid" : "parent", kind: openTo }); }, [mode, openTo]);

  const all = useMemo(() => allFaqs(), []);
  const visible = useMemo(() => faqsForAudience(all, audience), [all, audience]);
  const results = useMemo(() => searchFaqs(visible, query), [visible, query]);
  const groups = useMemo(() => groupByCategory(visible), [visible]);

  // One event per settled query rather than one per keystroke — 400ms after typing
  // stops. Carries success/failure only, never the text: a miss count is all we need to
  // know which answer to write next, and it keeps free text (a child's especially) out
  // of the dataset entirely.
  useEffect(() => {
    if (!query.trim()) return undefined;
    const timer = setTimeout(() => {
      track("faq_search", { success: results && results.length ? "true" : "false" });
    }, 400);
    return () => clearTimeout(timer);
  }, [query, results]);

  function toggleEntry(entry) {
    const next = openId === entry.id ? null : entry.id;
    setOpenId(next);
    if (next) track("faq_opened", { kind: entry.id });
  }

  function toggleCat(id) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const AUDIENCE_TABS = [
    { id: "kid", key: "help.audience_kid", label: "For kids" },
    { id: "parent", key: "help.audience_parent", label: "For grown-ups" },
    { id: "all", key: "help.audience_all", label: "Everything" },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* ANCHORED (Alan, 1 Oct): "anchor the FAQ overlay on the screen so that the overlay doesn't
          continuously resize". The card used to be as tall as whatever it held, centred, so every
          category opened, answer unfolded or search typed made it grow, shrink and jump. It is
          now one fixed height, most of the screen, with the title bar still and only the content
          beneath scrolling. The class sets it in vh; the inline dvh overrides it where a browser
          knows that unit, so a phone's address bar cannot cut the bottom off, and is simply
          ignored where it does not. */}
      <div className="bg-white rounded-2xl w-full max-w-md h-[85vh] flex flex-col overflow-hidden shadow-2xl" style={{ height: "85dvh" }} data-testid="help-modal">
        <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
          <div className="font-bold text-slate-800">
            {view === "report" ? ct("report.title", "Tell us what's up") : ct("help.title", "Help & support")}
          </div>
          <button onClick={onClose} aria-label="Close help" className="text-slate-400 text-2xl leading-none px-1">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="help-scroll">

        {view === "report" ? (
          <div className="p-4 flex flex-col gap-3">
            <ReportForm
              code={code}
              role={mode === "child" ? "child" : "parent"}
              ownerEmail={ownerEmail}
              defaultKind={openTo === "report" && errorHint ? "broken" : "confusing"}
              searchQuery={query}
              errorHint={errorHint}
              showToast={showToast}
              onDone={() => setView("faq")}
            />
            <button className="text-sm font-bold text-slate-500 underline self-start" onClick={() => setView("faq")}><Icon id="ic-back" size={14} className="inline-block align-[-2px] mr-1" style={{ color: "currentColor" }} />{ct("help.back_to_help", "Back to help")}</button>
          </div>
        ) : (
          <>
            {/* Pinned above the fold, not buried at the bottom of seventy answers. The
                whole point of this build is that the people who most need to reach us
                are the ones least likely to keep scrolling. */}
            <div className="px-4 pt-4">
              <button
                data-testid="help-ask-bar"
                onClick={() => setView("report")}
                className="w-full text-left rounded-lg bg-white border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-800"
              >
                {ct("help.ask_bar", "Can't find it, or something not making sense? Tell us")}
              </button>
            </div>

            <div className="px-4 pt-3">
              <input
                type="search"
                data-testid="help-search"
                aria-label="Search help"
                placeholder={ct("help.search_placeholder", "Search help, for example \"streak\" or \"vault\"")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-base"
              />
              <div className="flex gap-1.5 mt-2">
                {AUDIENCE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    data-testid={`help-audience-${tab.id}`}
                    aria-pressed={audience === tab.id}
                    onClick={() => { setAudience(tab.id); setOpenId(null); }}
                    className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-bold ${audience === tab.id ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}
                  >
                    {ct(tab.key, tab.label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 flex flex-col gap-3">
              {results ? (
                results.length ? (
                  <div className="flex flex-col gap-2" data-testid="help-results">
                    {results.map((entry) => (
                      <FaqItem
                        key={entry.id}
                        entry={entry}
                        open={openId === entry.id}
                        onToggle={() => toggleEntry(entry)}
                        onFeedback={(id, helpful) => track("faq_feedback", { kind: id, success: helpful ? "true" : "false" })}
                      />
                    ))}
                  </div>
                ) : (
                  // A failed search is the single best signal we get about missing
                  // content, so it routes straight into a report carrying the query.
                  <div className="rounded-xl border border-slate-200 px-3 py-4 text-center" data-testid="help-no-results">
                    <p className="text-sm text-slate-500 mb-2">{ct("help.no_results", "Nothing matches \"{query}\" yet.", { query: query.trim() })}</p>
                    <button className="text-sm font-bold text-slate-900 underline" onClick={() => setView("report")}>
                      {ct("help.no_results_cta", "Tell us what you were looking for")}
                    </button>
                  </div>
                )
              ) : (
                groups.map((group) => {
                  const open = openCats.has(group.id);
                  return (
                    <div key={group.id} className="border border-slate-100 rounded-xl overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between text-left px-3 py-2.5 bg-slate-50"
                        aria-expanded={open}
                        data-testid={`help-cat-${group.id}`}
                        onClick={() => toggleCat(group.id)}
                      >
                        <span className="text-sm font-semibold text-slate-700">{group.label}</span>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{group.items.length} <Icon id={open ? "ic-chevron-up" : "ic-chevron-down"} size={14} className="inline-block" style={{ color: "currentColor" }} /></span>
                      </button>
                      {open && (
                        <div className="p-2 flex flex-col gap-2">
                          {group.items.map((entry) => (
                            <FaqItem
                              key={entry.id}
                              entry={entry}
                              open={openId === entry.id}
                              onToggle={() => toggleEntry(entry)}
                              onFeedback={(id, helpful) => track("faq_feedback", { kind: id, success: helpful ? "true" : "false" })}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
